import { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';

/**
 * Server feature switches (GET /api/features), read once per page load.
 *
 * smartUploadEnabled — kyc.smart-upload.enabled on the API. false: the Smart Upload window
 * (journey Documents Center, mode=upload) is never offered; investors upload on the journey's KYC
 * Documents page. The journey-less Documents Center ("My Documents": list + View) stays, without
 * the OCR consent banner (decision 2026-10-23).
 * If the call fails the webapp behaves as today (enabled) — the API itself still refuses
 * Smart Upload / OCR when the server switch is off (decision 2026-10-23).
 */
export interface FeatureFlags {
  smartUploadEnabled: boolean;
}

const FALLBACK: FeatureFlags = { smartUploadEnabled: true };

let cached: FeatureFlags | null = null;
let pending: Promise<FeatureFlags> | null = null;

export function loadFeatureFlags(): Promise<FeatureFlags> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = apiClient
      .get<Partial<FeatureFlags>>('/api/features')
      .then((res) => {
        const data = res?.data ?? {};
        cached = { smartUploadEnabled: data.smartUploadEnabled !== false };
        return cached;
      })
      .catch(() => {
        pending = null; // retry on the next page that asks
        return FALLBACK;
      });
  }
  return pending;
}

/** true / false once known; null while loading (callers hide Smart Upload entry points until known). */
export function useSmartUploadEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(cached ? cached.smartUploadEnabled : null);
  useEffect(() => {
    let alive = true;
    loadFeatureFlags().then((f) => {
      if (alive) setEnabled(f.smartUploadEnabled);
    });
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}
